// src/components/gateway/symbolGrid/icons.ts
// Shared SVG icon paths — all Lucide-style (stroke-based, 24x24 viewBox)

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Create an SVG element with standard Lucide attributes */
export function createSvg(size: number = 14): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  return svg;
}

/** Create an SVG path element */
function path(d: string): SVGPathElement {
  const p = document.createElementNS(SVG_NS, 'path');
  p.setAttribute('d', d);
  return p;
}

/** Create an SVG circle element */
function circle(cx: number, cy: number, r: number): SVGCircleElement {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', String(cx));
  c.setAttribute('cy', String(cy));
  c.setAttribute('r', String(r));
  return c;
}

/** Create an SVG rounded rect element */
function rect(w: number, h: number, x: number, y: number, r: number): SVGRectElement {
  const el = document.createElementNS(SVG_NS, 'rect');
  el.setAttribute('width', String(w));
  el.setAttribute('height', String(h));
  el.setAttribute('x', String(x));
  el.setAttribute('y', String(y));
  el.setAttribute('rx', String(r));
  el.setAttribute('ry', String(r));
  return el;
}

// ---- Chevron icons ----

export const CHEVRON_DOWN_D = 'm7 15 5 5 5-5';

/** Create a chevron SVG (for tree expand/collapse) */
export function createChevronSvg(): { svg: SVGSVGElement; pathEl: SVGPathElement } {
  const svg = createSvg(14);
  svg.classList.add('vg-chevron');
  const p = path(CHEVRON_DOWN_D);
  svg.appendChild(p);
  return { svg, pathEl: p };
}

// ---- Priority (Lucide circle-alert) "!" icon ----
// Drives the per-row priority-polling toggle. Inherits color via currentColor,
// so the grid turns it red by setting the button's `color` when priority is on.
export function createAlertSvg(): SVGSVGElement {
  const svg = createSvg(14);
  svg.appendChild(circle(12, 12, 10));
  svg.appendChild(path('M12 8v4')); // the "!" stem
  svg.appendChild(path('M12 16h.01')); // the "!" dot
  return svg;
}

// ---- Lock / unlock padlock (Lucide lock-keyhole / lock-keyhole-open) ----
// Body rect + keyhole circle are constant; only the shackle path differs between
// states, so we swap the shackle `d` rather than rebuild the SVG.
const LOCK_OPEN_SHACKLE = 'M7 10V7a5 5 0 0 1 9.33-2.5';
const LOCK_CLOSED_SHACKLE = 'M7 10V7a5 5 0 0 1 10 0v3';
export function createLockSvg(): { svg: SVGSVGElement; setLocked: (locked: boolean) => void } {
  const svg = createSvg(14);
  svg.appendChild(rect(18, 12, 3, 10, 2)); // padlock body
  svg.appendChild(circle(12, 16, 1)); // keyhole
  const shackle = path(LOCK_OPEN_SHACKLE);
  svg.appendChild(shackle);
  return {
    svg,
    setLocked: (locked: boolean) => shackle.setAttribute('d', locked ? LOCK_CLOSED_SHACKLE : LOCK_OPEN_SHACKLE),
  };
}

// ---- Square-pen icon (value-edit button on writable rows; Lucide square-pen) ----

export function createSquarePenSvg(): SVGSVGElement {
  const svg = createSvg(14);
  svg.appendChild(path('M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'));
  svg.appendChild(path('M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z'));
  return svg;
}

// ---- Trash icon ----

export function createTrashSvg(): SVGSVGElement {
  const svg = createSvg(14);
  svg.appendChild(path('M10 11v6'));
  svg.appendChild(path('M14 11v6'));
  svg.appendChild(path('M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6'));
  svg.appendChild(path('M3 6h18'));
  svg.appendChild(path('M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'));
  return svg;
}

// ---- Search icon ----

export function createSearchSvg(): SVGSVGElement {
  const svg = createSvg(14);
  svg.appendChild(circle(11, 11, 8));
  svg.appendChild(path('m21 21-4.35-4.35'));
  return svg;
}

// ---- Clear (X circle) icon ----

export function createClearSvg(): SVGSVGElement {
  const svg = createSvg(14);
  svg.appendChild(circle(12, 12, 10));
  svg.appendChild(path('m15 9-6 6'));
  svg.appendChild(path('m9 9 6 6'));
  return svg;
}

// ---- Expand/Collapse All (double chevron) ----
// chevrons-up-down (expand): arrows point outward
const EXPAND_PATHS = ['m7 15 5 5 5-5', 'm7 9 5-5 5 5'];
// chevrons-down-up (collapse): arrows point inward
const COLLAPSE_PATHS = ['m7 20 5-5 5 5', 'm7 4 5 5 5-5'];

export function createExpandCollapseSvg(): { svg: SVGSVGElement; setExpanded: (expanded: boolean) => void } {
  const svg = createSvg(14);
  const TRANS = 'opacity 200ms ease, d 200ms ease';

  // Create both path sets — cross-fade between them
  const expandGroup = EXPAND_PATHS.map(d => {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    p.style.opacity = '0';
    p.style.transition = TRANS;
    svg.appendChild(p);
    return p;
  });

  const collapseGroup = COLLAPSE_PATHS.map(d => {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    p.style.opacity = '1';
    p.style.transition = TRANS;
    svg.appendChild(p);
    return p;
  });

  return {
    svg,
    setExpanded(expanded: boolean) {
      // Cross-fade: show one group, hide the other
      const showExpand = !expanded;
      expandGroup.forEach(p => p.style.opacity = showExpand ? '1' : '0');
      collapseGroup.forEach(p => p.style.opacity = showExpand ? '0' : '1');
    },
  };
}

// ---- Sort arrow icons (for column header sorting) ----

// Lucide MoveUp: full-length shaft + arrowhead at top
const MOVE_UP_SHAFT = 'M12 2V22';
const MOVE_UP_HEAD = 'M8 6L12 2L16 6';
// Lucide MoveDown: full-length shaft + arrowhead at bottom
const MOVE_DOWN_SHAFT = 'M12 2V22';
const MOVE_DOWN_HEAD = 'M8 18L12 22L16 18';

/** Create a sort arrow container with both up and down arrows inside.
 *  Only one is visible at a time via CSS class toggling.
 *  Uses Lucide MoveUp/MoveDown (full-length shaft) for better visibility. */
export function createSortArrowSvg(): { svg: SVGSVGElement } {
  const svg = createSvg(14);
  svg.classList.add('vg-sort-arrow');

  // MoveUp group (two paths: shaft + arrowhead)
  const upG = document.createElementNS(SVG_NS, 'g');
  upG.classList.add('vg-arrow-up');
  upG.appendChild(path(MOVE_UP_SHAFT));
  upG.appendChild(path(MOVE_UP_HEAD));
  svg.appendChild(upG);

  // MoveDown group (two paths: shaft + arrowhead)
  const downG = document.createElementNS(SVG_NS, 'g');
  downG.classList.add('vg-arrow-down');
  downG.appendChild(path(MOVE_DOWN_SHAFT));
  downG.appendChild(path(MOVE_DOWN_HEAD));
  svg.appendChild(downG);

  return { svg };
}
